import { AbstractControl, FormArray, FormControl, FormGroup, ValidatorFn } from "@angular/forms";
import { eActionState, eContactCardType, eContactVenueLocation, eCountry, eCurrency, eRadiusUnit, eContactType, eGender, eContactAgeRange, eContactSection, eSearchContactType } from "@hub/generated/enums";
import { AddressDto, AmountDto, ContactCardDto, CurrencyDto, VenueContactCardDto, SearchContactsOptions, FilterCollection } from "@hub/generated/models";
import { clone, cloneDeep, isEqual } from 'lodash';
import * as moment from "moment-timezone";
import { SelectItem } from "primeng/api";
import { Constants } from "./Constants";
import { EnumTypeDto } from "../_generated/models";

export class Functions {
  // for currency formatting eg (1,000,0)
  public static numberWithCommas(x): number {
    let parts = x.toString().split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    return parts.join(".");
  }

  public static isNumber(text: any): boolean {
    return parseFloat(text).toString() === text.toString();
  }

  public static booleanPipe(value: Boolean) {
    if (value === true) {
      return "Yes";
    } else if (value === false) {
      return "No";
    } else {
      return "--";
    }
  }

  public static getRegexMatch(regex: RegExp, text: string): Array<string> {
    let matches: RegExpExecArray;
    const result: Array<string> = [];

    while ((matches = regex.exec(text)) !== null) {
      // This is necessary to avoid infinite loops with zero-width matches
      if (matches.index === regex.lastIndex) {
        regex.lastIndex++;
      }

      // The result can be accessed through the `matches`-variable.
      matches.forEach((match, groupIndex) => {
        result.push(match);
      });
    }

    return result;
  }

  public static nameOf<T>(propertyExpression: (property: T) => any): string {
    const functionString = propertyExpression.toString();
    const start = functionString.lastIndexOf(".") + 1;
    const end = functionString.lastIndexOf(";");

    return functionString.substr(start, end - start);
  }

  public static IterateEnum(input: any, ...exludedValues): SelectItem[] {
    if (!input) {
      return null;
    }

    const parsed = this.getNamesAndValues(input);
    parsed.forEach(e => (e.label = this.toSentenceCase(e.label)));

    if (
      !!exludedValues &&
      Array.isArray(exludedValues) &&
      exludedValues.length > 0
    ) {
      return parsed.filter(item => exludedValues[0].indexOf(item.value) === -1);
    }

    return parsed;
  }

  public static toSentenceCase(e: string, space: boolean = true): string {
    if (space) {
      return e.replace(/([A-Z][a-z]|^[a-z]?)/g, " $1").replace(/[_]/g, " ");
    }

    return e.replace(/[_]/g, " ");
  }

  public static getNamesAndValues(e: any, space: boolean = true): SelectItem[] {
    const result: SelectItem[] = [];
    this.getNames(e).map(n =>
      result.push({ label: Functions.toSentenceCase(n, space), value: e[n] })
    );

    return result;
  }

  public static getNames(e: any) {
    let names = this.getObjValues(e).filter(
      v => typeof v === "string"
    ) as string[];

    return names;
  }

  public static getValues(e: any) {
    return this.getObjValues(e).filter(v => typeof v === "number") as number[];
  }

  public static getObjValues(e: any): (number | string)[] {
    return Object.keys(e).map(k => e[k]);
  }

  public static getEnumValue(e: any, key: any): number {
    if (!e || !key) {
      return undefined;
    }

    return this.getNamesAndValues(e)
      .filter(i => i.label.trim() === (key as string))
      .map(i => i.value)[0] as number;
  }

  public static getEnumName(
    e: any,
    value: number,
    space: boolean = true
  ): string {
    if (!e || !value) {
      return undefined;
    }

    return this.getNamesAndValues(e, space)
      .filter(i => i.value === (value as number))
      .map(i => i.label)[0] as string;
  }

  public static getEnumNormalizeName(e: any, value: number) {
    let enumName = this.getEnumName(e, value);

    return enumName ? this.toSentenceCase(enumName) : "";
  }

  public static cloneObject(obj: any) {
    const cln = cloneDeep(obj); // JSON.parse(JSON.stringify(obj));

    // Functions.cloneObjectArray(obj, cln);

    return cln;
  }

  public static getTimeZoneValue(e, value): string {

    if (!e || !value) {
      return undefined;
    }

    var result = this.getNamesAndValues(e)
      .filter(i => i.value === (value as number))
      .map(i => i.label)[3] as string;

    return "text"
  }

  public static formatHtmlNL(str: string): String {
    return str.replace(/\n/g, "<br>");
  }

  public static isEqual(a, b): Boolean {
    return isEqual(a, b);
  }

  public static formatEnumText(enumText: any): string {
    if (!enumText) {
      return;
    }

    return enumText.replace(/(\_)/g, " ").replace(/([A-Z])/g, " $1");
  }

  public static formatEnumTextField(enumText: any): string {
    if (!enumText) {
      return;
    }

    return enumText.replace(/_/g, " ");
  }

  public static stringToNullBoolean(value: string) {
    if (value) {
      if (value.toLowerCase() === Constants.BOOLEAN_TRUE) {
        return true;
      } else if (value.toLowerCase() === Constants.BOOLEAN_FALSE) {
        return false;
      }
    }

    return null;
  }

  public static booleanToString(value: boolean) {
    if (value === true) {
      return Constants.BOOLEAN_TRUE;
    } else if (value === false) {
      return Constants.BOOLEAN_FALSE;
    } else {
      return Constants.NULL;
    }
  }

  public static fromMetersConversion(toUnit: eRadiusUnit, value: number) {
    switch (toUnit) {
      case eRadiusUnit.Kilometers: {
        return value * Constants.METERS_TO_KILOMETER_RATIO;
      }
      case eRadiusUnit.Miles: {
        return value * Constants.METERS_TO_MILE_RATIO;
      }
      default: {
        return 0.0;
      }
    }
  }

  public static chunk(items: Array<Object>, length: number) {
    let result = [];
    let i = 0;
    let n = items.length;

    while (i < n) {
      result.push(items.slice(i, (i += length)));
    }

    return result;
  }

  public static GetInstaceAmountDto(): AmountDto {
    return <AmountDto>{
      Amount: 0,
      Currency: Functions.GetInstnceCurrency(),
    };
  }

  public static currencyNegativeValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (control.value) {
        const forbidden = control.value < 0;

        return forbidden ? { invalidentry: { value: control.value } } : null;
      } else {
        return null;
      }
    };
  }

  public static GetInstnceCurrency(): CurrencyDto {
    return <CurrencyDto>{
      Currency: eCurrency.USD,
      ExchangeRate: 0,
      Name: "U.S. Dollars",
      ShortName: "USD",
      Symbol: "$"
    };
  }

  public static GetInstaceContactCardDto(): ContactCardDto {
    return <ContactCardDto>{
      Id: Constants.GUID_EMPTY,
      Initials: '',
      Departments: '',
      Avatar: "",
      FullName: "",
      Phone: "",
      Email: "",
      Company: '',
      ContactType: eContactCardType.Client,
      Address: {
        CountryId: eCountry.Not_Set,
        Country: "",
        AddressLine1: "",
        City: "",
        State: "",
        PostalCode: "",
        Comment: ""
      },
      Agents: []
    };
  }

  public static GetInstanceSearchContactsOptions(search: string, types?: eSearchContactType[]): SearchContactsOptions {
    return <SearchContactsOptions>{
      Search: search,
      Section: eContactSection.Client,
      Types: types || [],
      Initials: "",
      NickName: "",
      FirstName: "",
      LastName: "",
      PhoneNumber: "",
      CompanyName: "",
      ContactNumber: "",
      Gender: eGender.Not_Set,
      Industries: "",
      WorkAreas: [],
      WorkTypes: [],
      BuyerMarketTypes: [],
      Offices: [],
      Departments: [],
      Ethnicities: [],
      Nationalities: [],
      AgeRanges: eContactAgeRange.Not_Set,
      DemographicAppeal: "",
      BasedIn: "",
      Tags: "",
      UserLists: "",
      City: "",
      State: "",
      Country: eCountry.Not_Set,
      DobFrom: "",
      DobTo: "",
      Assistant: "",
      Associate: "",
      Position: "",
      TaxNumbers: "",
      Loanouts: "",
      SkipContactIds: "",
      ActiveClientsOnly: null,
      AgentsOnly: null,
      PrivateOnly: null,
      RestrictedContactIds: "",
      IncludeArchived: null,
      IsPending: null
    };
  }

  public static GetInstanceSearchConnectContactsOptions(filterValues: FilterCollection, contactType: eSearchContactType[]): SearchContactsOptions {

    var dto: SearchContactsOptions;

    if (filterValues.List.filter(x => x.ColumnName == "FullName")) {
      var search = filterValues.List.filter(x => x.ColumnName == "FullName")[0].List[0].Value;

      dto = this.GetInstanceSearchContactsOptions(search, contactType);
    } else {
      dto = this.GetInstanceSearchContactsOptions("", contactType);
    }

    filterValues.List.forEach(data => {
      if (data.ColumnName == "Country") {
        dto.Country = Functions.getEnumValue(eCountry, data.List[0].Value);
      } else if (data.ColumnName == "State") {
        dto.State = data.List[0].Value;
      } else if (data.ColumnName == "City") {
        dto.City = data.List[0].Value;
      } else if (data.ColumnName == "Company") {
        dto.CompanyName = data.List[0].Value;
      }
    })

    return dto;
  }

  public static GetInstaceVenueContactCardDto(
    address: any
  ): VenueContactCardDto {
    return <VenueContactCardDto>{
      Location: eContactVenueLocation.USA,
      ContactType: eContactCardType.Venue,
      Id: Constants.GUID_EMPTY,
      Avatar: "",
      FullName: "",
      Phone: "",
      Email: "",
      Address:
        address != null
          ? address
          : {
            CountryId: eCountry.Not_Set,
            Country: '',
            Street: '',
            City: '',
            State: '',
            ZipCode: '',
            Comment: ''
          },
      Agents: []
    };
  }

  public static GetInstaceAddressDto(): AddressDto {
    return <AddressDto>{
      AddressString: "",
      AddressStringFromGoogle: null,
      ContactCardType: eContactType.Not_Set,
      CountryShort: "",
      GoogleMapsUrl: "",
      Id: Constants.GUID_EMPTY,
      Latitude: 0,
      Longitude: 0,
      CountryId: eCountry.Not_Set,
      Country: "",
      AddressLine1: "",
      City: "",
      State: "",
      PostalCode: "",
      Comment: ""
    };
  }

  public static getDeleteMessageWithNumber(
    // tslint:disable-next-line:variable-name
    number: number,
    message: string
  ): string {
    return message.replace("{number}", number.toString());
  }

  public static getDeactivateMessageWithNumber(
    // tslint:disable-next-line:variable-name
    number: number,
    message: string
  ): string {
    return message.replace("{number}", number.toString());
  }


  public static contactPickerValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (control.value) {
        const forbidden = control.value.Id === undefined;

        return forbidden ? { "invalid entry": { value: control.value } } : null;
      } else {
        return null;
      }
    };
  }

  public static getActionStateById(id: string): eActionState {
    return id == undefined || id == null || id == "" || id == Constants.GUID_EMPTY ? eActionState.Inserted : eActionState.Changed;
  }

  public static getActionState(id: string, actionState: eActionState): eActionState {
    return actionState == eActionState.Inserted || id == undefined || id == null || id == "" || id == Constants.GUID_EMPTY
      ? eActionState.Inserted
      : eActionState.Changed;
  }

  static newGuid() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);

      return v.toString(16);
    });
  }

  static isGuidEmpty(value: string) {
    return value == undefined || value == null || value == "" || value == Constants.GUID_EMPTY;
  }

  static IsStringEmpty(text: string) {
    return text == null || text == undefined || text.trim() == "";
  }

  public static openDealMemo(dealId, dealBookingId) {
    if (!(dealId == Constants.GUID_EMPTY || dealBookingId == Constants.GUID_EMPTY || dealId == null || dealBookingId == null)) {
      window.open(`/bookings/memo/(deals:${dealId}//bookings:${dealBookingId})`);

      return true;
    } else {
      return false;
    }
  }

  public static openDealBooking(dealId) {
    if (!(dealId == Constants.GUID_EMPTY || dealId == null)) {
      window.open(`/bookings/${dealId}`);

      return true;
    } else {
      return false;
    }
  }

  public static validateAllFormFields(formGroup: any) {
    // This code also works in IE 11
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);

      if (control instanceof FormControl) {
        control.markAsTouched({ onlySelf: true });
      } else if (control instanceof FormGroup) {
        this.validateAllFormFields(control);
      } else if (control instanceof FormArray) {
        this.validateAllFormFields(control);
      }
    });
  }

  public static isFieldInvalid(formControl: FormControl): boolean {
    return (
      formControl.invalid &&
      (formControl.touched || formControl.dirty)
    );
  }

  public static sortByLabel(items: SelectItem[]): SelectItem[] {
    return items.sort(function (l1, l2) {
      var label1 = l1.label.toUpperCase().replace(/\s/g, ""); // ignore upper and lowercase and remove blank spaces
      var label2 = l2.label.toUpperCase().replace(/\s/g, ""); // ignore upper and lowercase and remove blank spaces
      if (label1 < label2) {
        return -1;
      }

      if (label1 > label2) {
        return 1;
      }

      // names must be equal
      return 0;
    });
  }

  public static sortByValue(items: SelectItem[]): SelectItem[] {
    return items.sort(function (l1, l2) {
      var value1 = l1.value; // ignore upper and lowercase and remove blank spaces
      var value2 = l2.value; // ignore upper and lowercase and remove blank spaces
      if (value1 < value2) {
        return -1;
      }

      if (value1 > value2) {
        return 1;
      }

      // names must be equal
      return 0;
    });
  }

  private static cloneObjectArray(obj, cln) {
    const keys = Object.keys(obj);
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      if (Array.isArray(obj[key])) {
        cln[key] = [];

        for (let j = 0; j < obj[key].length; j++) {
          cln[key].push(clone(obj[key][j]));
        }
      }
    }
  }

  // todo
  public static GetEnumsFromLists(enums: EnumTypeDto, value?: number): string {
    if (!value)
      value = 0;

    return enums[enums.toString()].values[value].value;
  }

  public static GetEnumFromEnums(enums: EnumTypeDto) {
    return enums[enums.toString()];
  }

  public static GetArrayFirstValue(arr: any[] | null) {
    if (Array.isArray(arr) && arr.length > 0) {
      return arr[0];
    }

    return null;
  }

  public static DownloadBlobAsFile(blob, name?: string) {
    const fileURL = URL.createObjectURL(blob);

    var downloadLink = document.createElement("a");
    downloadLink.href = fileURL;

    if (name) {
      downloadLink.download = name;
    }

    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);

    URL.revokeObjectURL(fileURL);
  }
}
